import { playlistMembership, type Playlist, type QueueState } from '@visious/core';
import { fmt, text } from '../strings';

/**
 * Saving a list of the reader's own as a playlist, as reasoning: which playlists the picker offers, what
 * has to be read before anything is sent, and the one sentence the outcome line carries afterwards.
 *
 * Its own file rather than part of the hook beside it for `playlists/addModel.ts`'s reason: the dialog is
 * a `.tsx` and must not import `@visious/core` (R13), and the two decisions here are the ones worth
 * naming — how much the response already knows, and what the reader is told.
 */

export interface SaveTargetRow {
  readonly playlistId: string;
  readonly title: string;
  readonly countText: string | null;
  /** Reading the playlist out, sending the videos, or neither. */
  readonly state: 'idle' | 'reading' | 'saving';
  /** One save at a time: the rest of the rows decline while one of them is working. */
  readonly disabled: boolean;
}

/** The reader's own playlists as rows. `null` while the list has not arrived. */
export function saveTargetRows(
  playlists: readonly Playlist[] | null,
  working: { readonly playlistId: string; readonly state: 'reading' | 'saving' } | null,
): readonly SaveTargetRow[] | null {
  if (playlists === null) return null;
  return playlists.map((playlist): SaveTargetRow => {
    const title = playlist.title.trim();
    return {
      playlistId: playlist.playlistId,
      title: title === '' ? text().playlists.untitled : playlist.title,
      // `videoCount` rather than `entries.length`: the list endpoint caps the embedded rows at 100, so a
      // 340-entry playlist would otherwise report 100 (`playlists/model.ts` says the same).
      countText: fmt().countedNoun(playlist.videoCount, text().playlists.video),
      state: working?.playlistId === playlist.playlistId ? working.state : 'idle',
      disabled: working !== null,
    };
  });
}

/**
 * Whether what already arrived settles which of the queue's videos this playlist holds, so the save can
 * send without reading anything first.
 *
 * **The response often knows, and then the answer is free.** The owned-playlists list embeds up to 100
 * entries per playlist (§7g), so for a playlist no longer than that `playlistMembership` decides every
 * one of the queue's videos out of what is already in hand. A longer one has to be read out: the endpoint
 * accepts the same video twice without complaint, so guessing "not in it" is how a reader ends up with a
 * playlist holding one video twice out of a single press.
 *
 * Asked per queued video rather than about the playlist in general, because that is the shape
 * `playlistMembership` answers in — and a video sitting inside the embedded window is decided even where
 * the window is capped.
 */
export function playlistMembershipSettled(state: QueueState, playlist: Playlist): boolean {
  return !state.entries.some(
    (entry) => playlistMembership(playlist, entry.videoId) === 'unknown',
  );
}

/**
 * What the save did, as the sentence the queue's own outcome line shows for ~2.5 s.
 *
 * The count is pluralised on the **number** rather than on the formatted string (`countedNoun`), which
 * matters here for the smallest case there is: a queue of one video saved into a fresh playlist.
 */
export function savedText(added: number, already: number, playlistTitle: string): string {
  const words = text().watch.queue.save;
  if (added === 0) return words.nothingToSave(playlistTitle);
  const videos = fmt().countedNoun(added, text().playlists.video) ?? '';
  return words.saved(videos, already, playlistTitle);
}

/**
 * A refusal, and what had already landed when it came. The adds are one request each, so stopping at the
 * fourth of seven leaves three videos on the playlist — and a reader told "saved" about half a list has
 * been told something false.
 */
export function saveRefusedText(reason: string, added: number, playlistTitle: string): string {
  const words = text().watch.queue.save;
  if (added === 0) return words.refused(reason);
  const videos = fmt().countedNoun(added, text().playlists.video) ?? '';
  return words.refusedAfter(reason, videos, playlistTitle);
}
